'use client';

import { useState } from 'react';
import Link from 'next/link';
import { 
  Heart, 
  Edit3, 
  Trash2, 
  Clock, 
  Users, 
  Star,
  ChefHat,
  Package,
  ArrowRight,
  Plus
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@myapp/ui/components/tabs';
import { Checkbox } from '@myapp/ui/components/checkbox';

// 더미 북마크 데이터
const bookmarkedProducts = [
  { 
    id: 1, 
    type: 'product',
    name: '강원도 감자', 
    price: '15,000', 
    unit: '5kg', 
    image: '🥔', 
    tags: ['제철', '인기'], 
    origin: '평창',
    rating: 4.8,
    bookmarkedAt: '2024-09-10'
  },
  { 
    id: 2, 
    type: 'product',
    name: '평창 고랭지 배추', 
    price: '8,000', 
    unit: '1포기', 
    image: '🥬', 
    tags: ['신선', 'GAP인증'], 
    origin: '평창',
    rating: 4.7,
    bookmarkedAt: '2024-09-09'
  },
  { 
    id: 3, 
    type: 'product',
    name: '횡성 한우', 
    price: '89,000', 
    unit: '600g', 
    image: '🥩', 
    tags: ['프리미엄', '1++등급'], 
    origin: '횡성',
    rating: 4.9,
    bookmarkedAt: '2024-09-08'
  }
];

const bookmarkedRecipes = [
  { 
    id: 1, 
    type: 'recipe',
    title: '강원도 감자전', 
    difficulty: '쉬움', 
    time: '30분', 
    servings: 4, 
    image: '🥔', 
    rating: 4.8, 
    tags: ['전통요리', '간단'],
    bookmarkedAt: '2024-09-11'
  },
  { 
    id: 2, 
    type: 'recipe',
    title: '김치찌개 끝판왕', 
    difficulty: '보통', 
    time: '45분', 
    servings: 4, 
    image: '🍲', 
    rating: 4.9, 
    tags: ['찌개', '인기'],
    bookmarkedAt: '2024-09-10'
  },
  { 
    id: 3, 
    type: 'recipe',
    title: '한우 스테이크', 
    difficulty: '어려움', 
    time: '20분', 
    servings: 2, 
    image: '🥩', 
    rating: 4.7, 
    tags: ['특별요리', '고급'],
    bookmarkedAt: '2024-09-09'
  },
  { 
    id: 4, 
    type: 'recipe',
    title: '옥수수 크림스프', 
    difficulty: '쉬움', 
    time: '25분', 
    servings: 3, 
    image: '🌽', 
    rating: 4.6, 
    tags: ['간편', '제철'],
    bookmarkedAt: '2024-09-08'
  }
];

const difficultyColors = {
  '쉬움': 'bg-green-100 text-green-700 border-green-200',
  '보통': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  '어려움': 'bg-red-100 text-red-700 border-red-200'
};

export default function BookmarksPage() {
  const [activeTab, setActiveTab] = useState('products');
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  
  const currentItems = activeTab === 'products' ? bookmarkedProducts : bookmarkedRecipes;
  const isEmpty = currentItems.length === 0;

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    setSelectedItems([]);
  };

  const toggleItemSelection = (itemId: number) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAllItems = () => {
    if (selectedItems.length === currentItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentItems.map(item => item.id));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">즐겨찾기</h1>
              <p className="text-gray-600">저장한 상품과 레시피를 모아보세요</p>
            </div>
            
            {!isEmpty && (
              <Button
                variant={isEditMode ? "default" : "outline"}
                onClick={toggleEditMode}
                className="flex items-center gap-2"
              >
                <Edit3 className="w-4 h-4" />
                {isEditMode ? '완료' : '편집'}
              </Button>
            )}
          </div>

          {/* Edit Mode Actions */}
          {isEditMode && !isEmpty && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-4">
                <Checkbox
                  id="select-all"
                  checked={selectedItems.length === currentItems.length && currentItems.length > 0}
                  onCheckedChange={selectAllItems}
                />
                <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  전체 선택 ({selectedItems.length}/{currentItems.length})
                </label>
              </div>
              
              {selectedItems.length > 0 && (
                <Button variant="destructive" size="sm">
                  <Trash2 className="w-4 h-4 mr-1" />
                  삭제 ({selectedItems.length})
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-8">
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              상품
              <Badge variant="secondary" className="text-xs">
                {bookmarkedProducts.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="recipes" className="flex items-center gap-2">
              <ChefHat className="w-4 h-4" />
              레시피
              <Badge variant="secondary" className="text-xs">
                {bookmarkedRecipes.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            {bookmarkedProducts.length === 0 ? (
              // Empty State - Products
              <div className="text-center py-20">
                <div className="text-8xl mb-6">📦</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">저장한 상품이 없습니다</h3>
                <p className="text-gray-500 mb-6">
                  마음에 드는 강원도 특산물을 찾아서<br />
                  하트 버튼을 눌러 저장해보세요
                </p>
                <Link href="/seasonal">
                  <Button className="bg-green-500 hover:bg-green-600 text-white">
                    <Package className="w-4 h-4 mr-2" />
                    제철 특산물 둘러보기
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              // Product List
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bookmarkedProducts.map((product) => (
                  <Card key={product.id} className="group hover:shadow-lg transition-all overflow-hidden relative">
                    {isEditMode && (
                      <div className="absolute top-3 left-3 z-10">
                        <Checkbox
                          checked={selectedItems.includes(product.id)}
                          onCheckedChange={() => toggleItemSelection(product.id)}
                        />
                      </div>
                    )}
                    
                    {!isEditMode && (
                      <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 bg-white/90 hover:bg-white">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}

                    <Link href="/products/gangwon-potato-5kg">
                      <CardContent className="p-0">
                        <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center text-7xl">
                          {product.image}
                        </div>
                        
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <Heart className="w-4 h-4 text-red-500 fill-current mr-1" />
                              <span className="text-xs text-gray-500">{product.bookmarkedAt}</span>
                            </div>
                            <div className="flex items-center">
                              <Star className="w-4 h-4 text-yellow-500 fill-current mr-1" />
                              <span className="text-sm font-medium">{product.rating}</span>
                            </div>
                          </div>
                          
                          <h3 className="font-semibold text-gray-900 mb-1 truncate">{product.name}</h3>
                          <p className="text-xs text-gray-500 mb-2">{product.unit} · {product.origin}</p>
                          
                          <div className="flex flex-wrap gap-1 mb-3">
                            {product.tags.map((tag, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          
                          <p className="text-lg font-bold text-green-600">₩{product.price}</p>
                        </div>
                      </CardContent>
                    </Link>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="recipes">
            {bookmarkedRecipes.length === 0 ? (
              // Empty State - Recipes
              <div className="text-center py-20">
                <div className="text-8xl mb-6">👨‍🍳</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">저장한 레시피가 없습니다</h3>
                <p className="text-gray-500 mb-6">
                  맛있는 AI 레시피를 찾아서<br />
                  하트 버튼을 눌러 저장해보세요
                </p>
                <Link href="/recipes">
                  <Button className="bg-purple-500 hover:bg-purple-600 text-white">
                    <ChefHat className="w-4 h-4 mr-2" />
                    AI 레시피 둘러보기
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              // Recipe List
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bookmarkedRecipes.map((recipe) => (
                  <Card key={recipe.id} className="group hover:shadow-lg transition-all overflow-hidden relative">
                    {isEditMode && (
                      <div className="absolute top-3 left-3 z-10">
                        <Checkbox
                          checked={selectedItems.includes(recipe.id)}
                          onCheckedChange={() => toggleItemSelection(recipe.id)}
                        />
                      </div>
                    )}
                    
                    {!isEditMode && (
                      <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 bg-white/90 hover:bg-white">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}

                    <Link href="/recipes/gangwon-potato-jeon">
                      <CardContent className="p-0">
                        <div className="aspect-square bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center text-7xl">
                          {recipe.image}
                        </div>
                        
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <Heart className="w-4 h-4 text-red-500 fill-current mr-1" />
                              <span className="text-xs text-gray-500">{recipe.bookmarkedAt}</span>
                            </div>
                            <div className="flex items-center">
                              <Star className="w-4 h-4 text-yellow-500 fill-current mr-1" />
                              <span className="text-sm font-medium">{recipe.rating}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 mb-2">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${difficultyColors[recipe.difficulty as keyof typeof difficultyColors]}`}
                            >
                              {recipe.difficulty}
                            </Badge>
                          </div>
                          
                          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{recipe.title}</h3>
                          
                          <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {recipe.time}
                            </div>
                            <div className="flex items-center">
                              <Users className="w-3 h-3 mr-1" />
                              {recipe.servings}인분
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-1">
                            {recipe.tags.slice(0, 2).map((tag, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Link>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}